import {FC, MouseEvent} from "react";
import {Avatar, Badge, Button, Checkbox, Tooltip, Typography} from "antd";
import type {CheckboxChangeEvent} from "antd/es/checkbox";
import {BankOutlined, DeleteOutlined, UndoOutlined} from "@ant-design/icons";
import styled from "styled-components";
import {formatDistanceToNow} from "date-fns";
import {de} from "date-fns/locale";
import {IMessage} from "../../../services/types.ts";
import {colors} from "../../../util/colors.ts";
import {getInitials, LABEL_CONFIG} from "../constants.ts";

const {Text} = Typography;

interface IMessageItemProps {
    item: IMessage;
    selected?: boolean;
    onSelect?: (item: IMessage) => void;
    onToggleHidden?: (item: IMessage) => void;
    toggleHiddenLoading?: boolean;
    massSelected?: boolean;
    onToggleMassSelect?: (item: IMessage) => void;
    anyMassSelected?: boolean;
}

const getCandidateName = (item: IMessage): string => {
    if (item.contact_data === "visible" && item._candidate_2_0?.contact) {
        const {first_name, last_name} = item._candidate_2_0.contact;
        if (first_name || last_name) return `${first_name || ""} ${last_name || ""}`.trim();
    }
    if (item._candidate_2_0?.main_job?.job_position) {
        return `Anonymer Kandidat`;
    }
    return "Unbekannter Kandidat";
};

export const MessageItem: FC<IMessageItemProps> = ({
    item,
    selected,
    onSelect,
    onToggleHidden,
    toggleHiddenLoading,
    massSelected,
    onToggleMassSelect,
    anyMassSelected,
}) => {
    const candidateName = getCandidateName(item);
    const jobTitle = item._job_demand_with_labels?.[0]?.title
        || "Keine Stelle angehängt oder die Stelle wurde gelöscht";
    const lastMessage = item.message || "";
    const labelCfg = item.label ? LABEL_CONFIG[item.label] : null;
    const isHidden = !!item.client_hidden;

    const time = item.created_at
        ? formatDistanceToNow(new Date(item.created_at), {addSuffix: true, locale: de})
        : "";

    const handleToggleHidden = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onToggleHidden?.(item);
    };

    const handleMassSelectChange = (e: CheckboxChangeEvent) => {
        e.nativeEvent.stopPropagation();
        onToggleMassSelect?.(item);
    };

    const stop = (e: MouseEvent<HTMLElement>) => e.stopPropagation();

    return (
        <Row $selected={!!selected} onClick={() => onSelect?.(item)}>
            {onToggleMassSelect && (
                <SelectSlot $alwaysShow={!!anyMassSelected || !!massSelected} onClick={stop}>
                    <Checkbox checked={!!massSelected} onChange={handleMassSelectChange}/>
                </SelectSlot>
            )}
            <AvatarSlot>
                <Badge dot={false} offset={[-4, 4]}>
                    <Avatar
                        size={44}
                        style={{
                            backgroundColor: selected ? colors.main : "#bfbfbf",
                            fontSize: 14,
                            fontWeight: 600,
                        }}
                    >
                        {getInitials(candidateName)}
                    </Avatar>
                </Badge>
            </AvatarSlot>
            <Body>
                <TopRow>
                    <Text strong style={{fontSize: 14, color: colors.text}} ellipsis>
                        {candidateName}
                    </Text>
                    <TopRowRight>
                        <Text type="secondary" style={{fontSize: 12, flexShrink: 0}}>
                            {time}
                        </Text>
                        {onToggleHidden && (
                            <Tooltip title={isHidden ? "Wiederherstellen" : "Löschen"}>
                                <HoverIconButton
                                    type="text"
                                    size="small"
                                    danger={!isHidden}
                                    icon={isHidden ? <UndoOutlined/> : <DeleteOutlined/>}
                                    loading={toggleHiddenLoading}
                                    onClick={handleToggleHidden}
                                />
                            </Tooltip>
                        )}
                    </TopRowRight>
                </TopRow>
                <SubRow>
                    <BankOutlined style={{marginRight: 4, fontSize: 11}}/>
                    <Text type="secondary" style={{fontSize: 12}} ellipsis>
                        {jobTitle}
                    </Text>
                </SubRow>
                <Snippet ellipsis>{lastMessage}</Snippet>
                {labelCfg && (
                    <Chip $bg={labelCfg.bg} $color={labelCfg.color}>{labelCfg.label}</Chip>
                )}
            </Body>
        </Row>
    );
};

const Row = styled.div<{$selected: boolean}>`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    background: ${(p) => (p.$selected ? "#f0f0f0" : "#fff")};
    border-bottom: 1px solid #f0f0f0;
    border-left: 3px solid ${(p) => (p.$selected ? colors.main : "transparent")};
    transition: background 0.15s ease;

    &:hover {
        background: ${(p) => (p.$selected ? "#f0f0f0" : "#e6f4ff")};
    }
`;

const TopRowRight = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    margin-left: 8px;
`;

const HoverIconButton = styled(Button)`
    opacity: 0;
    transition: opacity 0.15s ease;

    ${Row}:hover & {
        opacity: 1;
    }

    &.ant-btn-loading {
        opacity: 1;
    }

    @media (max-width: 767px) {
        opacity: 1;
    }
`;

const AvatarSlot = styled.div`
    flex-shrink: 0;
`;

const SelectSlot = styled.div<{$alwaysShow: boolean}>`
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

const Body = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const TopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SubRow = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
`;

const Snippet = styled(Text)`
    && {
        font-size: 13px;
        color: #595959;
        display: block;
    }
`;

const Chip = styled.span<{$bg: string; $color: string}>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    align-self: flex-start;
    font-size: 11px;
    line-height: 18px;
    padding: 0 8px;
    border-radius: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    border: 1px solid ${(p) => p.$color}20;
    white-space: nowrap;
`;
